import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { LoggerService } from './logger.service';
import { ToastService } from './toast.service';
import { KeycloakUserManagementResponse } from '../shared/models/keycloak-user-management/keycloak-user-management-response.model';
import { KeycloakUserDetailResponse } from '../shared/models/keycloak-user-management/keycloak-user-detail-response.model';
import { AddKeycloakUserPayload } from '../shared/models/keycloak-user-management/add-keycloak-user-payload.model';
import { UpdateKeycloakUserPayload } from '../shared/models/keycloak-user-management/update-keycloak-user-payload.model';

export interface KeycloakUserPageResponse {
  totalPages: number;
  content: KeycloakUserManagementResponse[];
}

export class KeycloakUserManagementApiError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
    this.name = 'KeycloakUserManagementApiError';
  }
}

interface BackendErrorResponse {
  error?: string;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class KeycloakUserManagementService {
  private readonly _apiUrl = `${import.meta.env.NG_APP_API_URL}/backoffice/v1/keycloak-users`;

  constructor(
    private http: HttpClient,
    private loggerService: LoggerService,
    private toastService: ToastService
  ) {}

  getKeycloakUsers(params: HttpParams): Observable<KeycloakUserPageResponse> {
    return this.http
      .get<KeycloakUserPageResponse>(`${this._apiUrl}/active`, {
        params: this.withOrFilterCombination(params),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  getInactiveKeycloakUsers(params: HttpParams): Observable<KeycloakUserPageResponse> {
    return this.http
      .get<KeycloakUserPageResponse>(`${this._apiUrl}/inactive`, {
        params: this.withOrFilterCombination(params),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  getKeycloakUserDetails(id: string): Observable<KeycloakUserDetailResponse> {
    return this.http
      .get<KeycloakUserDetailResponse>(`${this._apiUrl}/${id}`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  addKeycloakUser(payload: AddKeycloakUserPayload): Observable<{ message: string }> {
    return this.http
      .post<{ message: string }>(`${this._apiUrl}/add`, payload)
      .pipe(catchError(this.handleError.bind(this)));
  }

  updateKeycloakUser(payload: UpdateKeycloakUserPayload): Observable<{ message: string }> {
    return this.http
      .post<{ message: string }>(`${this._apiUrl}/update`, payload)
      .pipe(catchError(this.handleError.bind(this)));
  }

  recreateKeycloakUser(payload: UpdateKeycloakUserPayload): Observable<{ message: string }> {
    return this.http
      .post<{ message: string }>(`${this._apiUrl}/recreate`, payload)
      .pipe(catchError(this.handleError.bind(this)));
  }

  deactivateKeycloakUser(id: string): Observable<{ message: string }> {
    return this.http
      .put<{ message: string }>(`${this._apiUrl}/${id}/deactivate`, null)
      .pipe(catchError(this.handleError.bind(this)));
  }

  reactivateKeycloakUser(id: string): Observable<{ message: string }> {
    return this.http
      .put<{ message: string }>(`${this._apiUrl}/${id}/reactivate`, null)
      .pipe(catchError(this.handleError.bind(this)));
  }

  deleteKeycloakUser(id: string): Observable<{ message: string }> {
    return this.http
      .delete<{ message: string }>(`${this._apiUrl}/${id}/delete`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  deleteDatabaseUser(id: string): Observable<{ message: string }> {
    return this.http
      .delete<{ message: string }>(`${this._apiUrl}/database/${id}`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  private withOrFilterCombination(params: HttpParams): HttpParams {
    return params.has('filterCombination')
      ? params
      : params.set('filterCombination', 'OR');
  }

  private handleError(error: HttpErrorResponse) {
    const backendErrorMessage = this.getBackendErrorMessage(error);
    let errorMessage = backendErrorMessage ?? 'Ein unbekannter Fehler ist aufgetreten!';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Ein Fehler ist aufgetreten: ${error.error.message}`;
    } else if (!backendErrorMessage) {
      errorMessage =
        `Backend hat mit Status ${error.status} geantwortet. ` +
        `Grund dafür ist: ${error.message}`;
    }
    this.loggerService.error(errorMessage);
    this.toastService.show('warn', 'Ein Fehler ist aufgetreten', errorMessage);
    return throwError(() => new KeycloakUserManagementApiError(errorMessage, error.status));
  }

  private getBackendErrorMessage(error: HttpErrorResponse): string | null {
    const responseError = error.error;

    if (typeof responseError === 'string') {
      try {
        return this.getMessageFromErrorBody(JSON.parse(responseError)) ?? responseError;
      } catch {
        return responseError;
      }
    }

    return this.getMessageFromErrorBody(responseError);
  }

  private getMessageFromErrorBody(responseError: unknown): string | null {
    if (responseError && typeof responseError === 'object') {
      const backendError = responseError as BackendErrorResponse;
      return backendError.error ?? backendError.message ?? null;
    }

    return null;
  }
}
